import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnInit, ViewChild } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DefaultIcon, SDate, SDuration } from '@summit/core';
import { DisplayType, NumberFormatter, SummitIdObject } from '@summit/models';
import { LayoutService } from '@summit/ui';
import dayjs, { Dayjs } from 'dayjs';
import { Duration } from 'dayjs/plugin/duration';
import {
  NgxMaterialTimepickerComponent,
  NgxMaterialTimepickerModule,
  NgxMaterialTimepickerTheme
} from 'ngx-material-timepicker';
import { Button } from 'primeng/button';
import { FloatLabel } from 'primeng/floatlabel';
import { InputGroup } from 'primeng/inputgroup';
import { InputGroupAddon } from 'primeng/inputgroupaddon';
import { InputText } from 'primeng/inputtext';
import { map, Observable, shareReplay, startWith, takeUntil } from 'rxjs';
import { SummitDialogDirective } from '../../directives';
import { SummitControl } from '../../models';
import { SFormErrorInputComponent } from '../s-form-error-input';

@Component({
  selector: 's-form-input-time',
  templateUrl: './s-form-input-time.component.html',
  styleUrls: ['./s-form-input-time.component.scss'],
  standalone: true,
  imports: [
    SFormErrorInputComponent,
    ReactiveFormsModule,
    FormsModule,
    InputGroupAddon,
    InputGroup,
    InputText,
    FloatLabel,
    Button,
    AsyncPipe,
    NgxMaterialTimepickerModule
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SFormInputTimeComponent<T extends SummitIdObject> extends SummitControl<T> implements OnInit {
  @Input() stepMinute = 15;
  @Input() max: Observable<Duration>;
  @Input() timeFormat: '12' | '24' = '12';
  @Input() format: 'duration' | 'time' = 'duration';
  @Input() showSeconds?: boolean; //currently not supported by NgxMaterialTimepicker, but could be added in the future if needed
  @Input() oneSecUnknown; //if true, then the time is unknown when the second value is set to 1
  @ViewChild('picker') picker: NgxMaterialTimepickerComponent;
  inputValue?: string;
  defaultIcon = DefaultIcon;
  maxDateString$: Observable<string | undefined>;
  sDialog = new SummitDialogDirective();
  pickerTheme$: Observable<NgxMaterialTimepickerTheme>;
  convertUtcToLocal: boolean;

  constructor(layout: LayoutService) {
    super();
    this.pickerTheme$ = layout.config$.pipe(
      //tap((config) => console.log('layout config', config.theme)),
      map((config) => (config.theme === 'dark-mode' ? this.darkTheme : this.lightTheme)),
      shareReplay(1)
    );
  }

  lightTheme: NgxMaterialTimepickerTheme = {
    container: {
      bodyBackgroundColor: '#ffffff', //surface
      buttonColor: '#3f51b5' //primary
    },
    dial: {
      dialBackgroundColor: '#3f51b5' //primary
      //dialInactiveColor: '#424242', //surface-300
      //dialActiveColor: '#424242', //surface-300
      //dialEditableActiveColor: '#424242', //surface-300
      //dialEditableBackgroundColor: '#424242' //surface-300
    },
    clockFace: {
      clockFaceBackgroundColor: '#e0e0e0', //surface-300
      clockHandColor: '#3f51b5', //primary
      clockFaceInnerTimeInactiveColor: '#424242' //surface-800
      //clockFaceTimeActiveColor: '#424242', //surface-300
      //clockFaceTimeInactiveColor: '#424242', //surface-300
      //clockFaceTimeDisabledColor: '#424242' //surface-300
    }
  };
  darkTheme: NgxMaterialTimepickerTheme = {
    container: {
      bodyBackgroundColor: '#1e1e1e', //surface
      buttonColor: '#292e4b' //primary
    },
    dial: {
      dialBackgroundColor: '#292e4b' //primary
      //dialInactiveColor: '#424242', //surface-300
      //dialActiveColor: '#424242', //surface-300
      //dialEditableActiveColor: '#424242', //surface-300
      //dialEditableBackgroundColor: '#424242' //surface-300
    },
    clockFace: {
      clockFaceBackgroundColor: '#414141', //surface-200 (different from light mode)
      clockHandColor: '#292e4b' //primary
      //clockFaceInnerTimeInactiveColor: '#e7e7e7' //surface-800
      //clockFaceTimeActiveColor: '#424242', //surface-300
      //clockFaceTimeInactiveColor: '#424242', //surface-300
      //clockFaceTimeDisabledColor: '#424242' //surface-300
    }
  };

  override ngOnInit() {
    super.ngOnInit();
    const attribute = this.schema ? this.schema?.refColumnList[this.fieldName ?? ''] : undefined;
    this.convertUtcToLocal =
      attribute?.displayType === DisplayType.Time ||
      attribute?.displayType === DisplayType.DateTimeLocal ||
      attribute?.displayType === DisplayType.DateTimeSecLocal;
    this.maxDateString$ = this.max?.pipe(
      map((value) => {
        //console.log('max value', value);
        if (!value || value?.asMinutes() === 0) return undefined;
        const lclDate = SDate.newUtc('1970-01-01');
        const lclTime = lclDate.add(value);
        const dateString = lclTime.format('h:mm A');
        return dateString;
      })
    );
    this.form.controls[this.fieldName ?? '']?.valueChanges
      .pipe(startWith(this.form.controls[this.fieldName ?? '']?.value), takeUntil(this.disposing$))
      .subscribe((value) => {
        //console.log(`${this.fieldName}: value changed`, value);
        if (SDate.isDayjs(value)) {
          const localValue = this.convertUtcToLocal ? SDate.tz(value, dayjs.tz.guess()) : value;
          //console.log(`${this.fieldName}: value changed to Dayjs`, this.oneSecUnknown, value.format());
          if (this.oneSecUnknown && value.second() === 1) {
            //console.log('setting time to unknown');
            this.inputValue = '';
          } else if (this.timeFormat === '24') {
            this.inputValue = localValue.format('H:mm');
          } else {
            this.inputValue = localValue.format('h:mm A');
          }
          this.cd.detectChanges();
        } else if (SDuration.isDuration(value)) {
          //console.log(`${this.fieldName}: value changed to duration`, value.format());
          if (this.timeFormat === '24') {
            //console.log('updating control with 24 hours', value.hours());
            this.inputValue = `${value.hours()}:${NumberFormatter(value.minutes(), 2)}`;
          } else {
            const hours = value.hours() % 12;
            const displayHours = hours === 0 ? 12 : hours;
            const response = `${displayHours}:${NumberFormatter(value.minutes(), 2)} ${
              value.hours() >= 12 ? 'PM' : 'AM'
            }`;
            //console.log('updating control with 12 hours', response);
            this.inputValue = response;
          }
          this.cd.detectChanges();
        } else if (value instanceof Date) {
          //write the entire date back to the form
          //console.log(`${this.fieldName}: updating value with Dayjs`);
          const sDate = SDate.newUtc(value);
          //console.log(`Publishing new value ${hours}:${minutes}`);
          this.setValueFromDayjs(sDate);
        } else if (!value) {
          this.inputValue = '';
          this.cd.detectChanges();
        }
      });
  }

  setValueFromDuration(lclDuration: Duration) {
    const fieldName = this.fieldName?.toString();
    if (!fieldName || !this.form.controls[fieldName]) return;
    const currentValue =
      this.format === 'duration'
        ? SDate.newUtc('1970-01-01')
        : this.convertUtcToLocal
          ? SDate.tz(SDate.newUtc(this.form.controls[this.fieldName ?? '']?.value), dayjs.tz.guess())
          : SDate.newUtc(this.form.controls[this.fieldName ?? '']?.value);
    let correctValue = currentValue.startOf('day').add(lclDuration);
    if (this.convertUtcToLocal) correctValue = SDate.tz(correctValue, 'utc');
    this.setValueFromDayjs(correctValue);
  }
  setValueFromDayjs(value: Dayjs) {
    const fieldName = this.fieldName?.toString();
    if (!fieldName || !this.form.controls[fieldName]) return;
    if (this.format === 'duration') {
      const diff = value.diff(value.startOf('day'));
      const lclDuration = dayjs.duration(diff);
      this.form.controls[this.fieldName ?? '']?.setValue(lclDuration);
    } else {
      this.form.controls[this.fieldName ?? '']?.setValue(value);
    }
  }

  onBlur(_) {
    this.onTimeSet(this.inputValue);
  }

  getObject(): Dayjs | Duration | undefined {
    const value = this.form.controls[this.fieldName ?? '']?.value;
    if (value != null && value != undefined) {
      if (SDate.isDayjs(value)) {
        return value;
      } else if (value instanceof Date) {
        return SDate.newUtc(value);
      }
      if (SDuration.isDuration(value)) {
        return SDuration.new(value, true);
      }
      return SDate.newUtc(value);
    }
    return undefined;
  }

  launchTimePicker() {
    console.log('time picker value');
    this.picker.open();
  }

  onTimeSet(time: string | undefined) {
    const rawString = time;
    //console.log('setting time to', rawString);
    if (rawString && rawString.length > 0) {
      //console.log('raw string: ', rawString);
      let lclDuration = SDuration.new(rawString, true);
      if (lclDuration.hours() == 12 && rawString.includes('AM')) {
        lclDuration = lclDuration.subtract(12, 'hours');
      } else if (lclDuration.hours() < 12 && rawString.includes('PM')) {
        lclDuration = lclDuration.add(12, 'hours');
      }
      //console.log(`setting time to ${lclDuration.hours()}:${lclDuration.minutes()}`, lclDuration);
      this.setValueFromDuration(lclDuration);
    } else {
      const fieldName = this.fieldName?.toString();
      if (!fieldName || !this.form.controls[fieldName]) return;
      if (this.oneSecUnknown) {
        const currentValue = this.form.controls[this.fieldName ?? '']?.value;
        if (SDate.isDayjs(currentValue)) {
          const newValue = currentValue.startOf('day').add(1, 'second');
          this.form.controls[this.fieldName ?? '']?.setValue(newValue);
        }
      } else {
        this.form.controls[this.fieldName ?? '']?.setValue(undefined);
      }
    }
  }

  clear($event: MouseEvent) {
    $event.preventDefault();
    $event.stopPropagation();
    this.form.controls[this.fieldName ?? '']?.setValue(undefined);
    this.inputValue = undefined;
  }
}
